// Wizard público de assinatura de contrato por autoatendimento (pedido
// 2026-09-11) — o cliente abre este link (enviado pelo admin via WhatsApp ou
// copiado manualmente, ver users.tsx aba CONTRATO) SEM login e confirma
// dados, localização, foto e assinatura (nome digitado + aceite) sozinho.
// Rota pública de propósito: ver bypass de AuthProvider/AuthGate em
// __root.tsx (isPublicStaticRoute cobre "/contrato/assinar/"). Nenhum
// componente do resto do app (AuthenticatedShell, Toaster) está montado
// aqui — mensagens de erro/sucesso são sempre inline na própria tela.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { CheckCircle2, Loader2, MapPin, Camera as CameraIcon, FileText, Maximize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { gpsDriveContractSigningService, type ContractSigningPreview } from "@/services/gpsdrive-contract-signing.service";
import { GpsDriveApiError, resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import type { ContractCompany } from "@/services/gpsdrive-contracts.service";
import defaultLogoUrl from "@/assets/gps-drive-logo-v3.png";
import { LicensePlate } from "@/components/license-plate";

export const Route = createFileRoute("/contrato/assinar/$token")({
  head: () => ({ meta: [{ title: "Assinatura de Contrato — GPS Drive" }] }),
  component: ContractSigningPage,
});

type Step = 1 | 2 | 3 | 4;

// Logo/nome = Administração → Contrato → Dados da empresa (2026-10-14).
function Shell({ children, company }: { children: React.ReactNode; company?: ContractCompany }) {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 flex flex-col items-center gap-1 text-center">
          {company?.logoUrl ? (
            <img src={resolveGpsDriveUrl(company.logoUrl)} alt={company.name} className="h-14 max-w-[220px] object-contain" />
          ) : !company ? (
            <img src={defaultLogoUrl} alt="" className="h-12" />
          ) : null}
          {company?.name && <div className="text-sm font-semibold text-slate-800">{company.name}</div>}
          {company && (company.phone || company.email) && (
            <div className="text-xs text-slate-500">{[company.phone, company.email].filter(Boolean).join(" · ")}</div>
          )}
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">{children}</div>
      </div>
    </div>
  );
}

function ContractSigningPage() {
  const { token } = Route.useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ContractSigningPreview | null>(null);
  const [step, setStep] = useState<Step>(1);
  const [signed, setSigned] = useState(false);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);

  useEffect(() => {
    let cancelled = false;
    gpsDriveContractSigningService
      .getByToken(token)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err instanceof GpsDriveApiError && err.status === 404
            ? "Este link de assinatura é inválido ou expirou."
            : "Não foi possível carregar os dados do contrato. Tente novamente em instantes.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 py-10 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">Carregando contrato…</p>
        </div>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <div className="py-6 text-center">
          <p className="text-sm text-slate-600">{loadError}</p>
        </div>
      </Shell>
    );
  }

  if (!preview) return null;

  if (preview.alreadySigned || signed) {
    return (
      <Shell company={preview.company}>
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-600" />
          <h1 className="text-lg font-semibold text-slate-900">Contrato assinado</h1>
          <p className="text-sm text-slate-500">
            {signed
              ? "Assinatura concluída com sucesso. Você já pode fechar esta página."
              : "Este contrato já foi assinado anteriormente."}
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell company={preview.company}>
      <StepIndicator step={step} />
      {step === 1 && <StepConfirmData preview={preview} onNext={() => setStep(2)} />}
      {step === 2 && (
        <StepLocation
          onBack={() => setStep(1)}
          onNext={(c) => {
            setCoords(c);
            setStep(3);
          }}
        />
      )}
      {step === 3 && (
        <StepPhoto
          token={token}
          onBack={() => setStep(2)}
          onNext={(f) => {
            setPhoto(f);
            setStep(4);
          }}
        />
      )}
      {step === 4 && (
        <StepSign
          preview={preview}
          token={token}
          coords={coords}
          photo={photo}
          onBack={() => setStep(3)}
          onSigned={() => setSigned(true)}
        />
      )}
    </Shell>
  );
}

function StepIndicator({ step }: { step: Step }) {
  const labels = ["Dados", "Localização", "Foto", "Assinatura"];
  return (
    <div className="mb-6 flex items-center justify-between gap-1">
      {labels.map((label, i) => {
        const n = (i + 1) as Step;
        const active = n === step;
        const done = n < step;
        return (
          <div key={label} className="flex flex-1 flex-col items-center gap-1">
            <div
              className={`grid h-7 w-7 place-items-center rounded-full text-xs font-medium ${
                done ? "bg-emerald-600 text-white" : active ? "bg-sky-600 text-white" : "bg-slate-200 text-slate-500"
              }`}
            >
              {done ? <CheckCircle2 className="h-4 w-4" /> : n}
            </div>
            <span className={`text-[10px] ${active ? "font-medium text-slate-900" : "text-slate-400"}`}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function StepConfirmData({ preview, onNext }: { preview: ContractSigningPreview; onNext: () => void }) {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Confirme seus dados</h1>
        <p className="mt-1 text-sm text-slate-500">Revise as informações abaixo antes de continuar.</p>
      </div>
      <dl className="space-y-2 rounded-lg bg-slate-50 p-4 text-sm">
        <Row label="Nome" value={preview.client.name} />
        {preview.client.cpfCnpj && <Row label="CPF/CNPJ" value={preview.client.cpfCnpj} />}
        {preview.client.mobilePhone && <Row label="Celular" value={preview.client.mobilePhone} />}
        {preview.client.address && <Row label="Endereço" value={preview.client.address} />}
        {preview.contractValue != null && (
          <Row label="Valor do contrato" value={formatCurrency(preview.contractValue)} />
        )}
        {preview.recurrenceValue != null && (
          <Row label="Valor da recorrência" value={formatCurrency(preview.recurrenceValue)} />
        )}
        {(preview.signatories ?? (preview.signatory ? [preview.signatory] : [])).map((s, i) => (
          <Row key={i} label={s.role === "testemunha" ? "Testemunha" : "Pela empresa"} value={s.name} />
        ))}
      </dl>
      {preview.vehicles.length > 0 && (
        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700">Veículo(s)</p>
          <div className="space-y-1.5">
            {preview.vehicles.map((v, i) => (
              <div key={i} className="rounded-lg bg-slate-50 p-3 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Placa</span>
                  <LicensePlate plate={v.plate} size="sm" />
                </div>
                {v.model && (
                  <div className="mt-1 flex justify-between gap-3">
                    <span className="text-slate-500">Modelo</span>
                    <span className="font-medium text-slate-900">{v.model}</span>
                  </div>
                )}
                <div className="mt-1 flex justify-between gap-3">
                  <span className="text-slate-500">Plano</span>
                  <span className="font-medium text-slate-900">{v.plan ?? "Sem plano"}</span>
                </div>
                {v.monthlyValue != null && (
                  <div className="mt-1 flex justify-between gap-3">
                    <span className="text-slate-500">Mensalidade</span>
                    <span className="font-medium text-slate-900">{formatCurrency(v.monthlyValue)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {preview.template && <ContractText template={preview.template} />}
      <Button className="w-full" onClick={onNext}>
        Confirmar dados
      </Button>
    </div>
  );
}

// Texto do contrato pro cliente (pedido 2026-10-14: "o cliente não
// consegue ler o modelo do contrato") — caixa com letra de leitura normal e
// botão de tela cheia; o leitor também abre do passo de assinatura.
function ContractText({ template }: { template: { title: string; content: string } }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          <FileText className="h-4 w-4" /> {template.title}
        </p>
      </div>
      <div className="max-h-[50vh] overflow-y-auto whitespace-pre-line rounded-lg border border-slate-200 bg-white p-4 text-[15px] leading-relaxed text-slate-800">
        {template.content}
      </div>
      <ContractReaderButton template={template} className="mt-2" />
    </div>
  );
}

function ContractReaderButton({
  template,
  className,
}: {
  template: { title: string; content: string };
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  return (
    <>
      <Button type="button" variant="outline" className={`w-full ${className ?? ""}`} onClick={() => setOpen(true)}>
        <Maximize2 className="mr-2 h-4 w-4" /> Ler contrato completo
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white" role="dialog" aria-modal="true" aria-label={template.title}>
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
            <h2 className="min-w-0 truncate text-base font-semibold text-slate-900">{template.title}</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-600 hover:bg-slate-100"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-2xl whitespace-pre-line px-5 py-6 text-base leading-7 text-slate-800">
              {template.content}
            </div>
          </div>
          <div className="border-t border-slate-200 p-3">
            <Button className="mx-auto block w-full max-w-2xl" onClick={() => setOpen(false)}>
              Voltar
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-900">{value}</dd>
    </div>
  );
}

function formatCurrency(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function StepLocation({
  onBack,
  onNext,
}: {
  onBack: () => void;
  onNext: (coords: { lat: number; lng: number }) => void;
}) {
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [coords, setLocalCoords] = useState<{ lat: number; lng: number } | null>(null);

  const requestLocation = () => {
    setStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocalCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setStatus("idle");
      },
      () => setStatus("error"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  useEffect(() => {
    requestLocation();
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Confirme sua localização</h1>
        <p className="mt-1 text-sm text-slate-500">
          Precisamos confirmar que você está assinando pelo próprio celular.
        </p>
      </div>
      <div className="flex flex-col items-center gap-3 rounded-lg bg-slate-50 py-8 text-center">
        <MapPin className={`h-8 w-8 ${coords ? "text-emerald-600" : "text-slate-400"}`} />
        {status === "loading" && <p className="text-sm text-slate-500">Obtendo localização…</p>}
        {status === "error" && (
          <>
            <p className="text-sm text-rose-600">
              Não foi possível obter sua localização. Verifique se a permissão de localização está
              liberada para o navegador e tente novamente.
            </p>
            <Button variant="outline" size="sm" onClick={requestLocation}>
              Tentar novamente
            </Button>
          </>
        )}
        {coords && status === "idle" && (
          <p className="text-sm font-medium text-slate-700">
            Localização confirmada ({coords.lat.toFixed(5)}, {coords.lng.toFixed(5)})
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onBack}>
          Voltar
        </Button>
        <Button className="flex-1" disabled={!coords} onClick={() => coords && onNext(coords)}>
          Continuar
        </Button>
      </div>
    </div>
  );
}

// Câmera ao vivo dentro da própria página (pedido 2026-09-11: "o sistema
// tem que deixar abrir a câmera dentro da assinatura") — getUserMedia +
// <video> + captura via <canvas>, em vez de só delegar pro seletor de
// arquivo do sistema operacional (que em alguns navegadores/WebViews mostra
// uma escolha Câmera/Galeria em vez de abrir a câmera direto). O <input
// type="file" capture="user"> permanece como fallback (getUserMedia
// indisponível, sem HTTPS, ou permissão negada) — nunca deixa a pessoa
// travada sem conseguir tirar a foto.
function StepPhoto({
  token,
  onBack,
  onNext,
}: {
  token: string;
  onBack: () => void;
  onNext: (file: File) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Verificação de rosto por IA (pedido 2026-09-11, engrenagem em
  // Administração > Contrato) — best-effort: `faceDetected === null` (API
  // não configurada, ou a chamada falhou) nunca trava o "Continuar";
  // `false` (o provedor respondeu e não achou rosto) trava até a pessoa
  // tirar outra foto.
  const [verifying, setVerifying] = useState(false);
  const [faceDetected, setFaceDetected] = useState<boolean | null>(null);
  const [faceCheckReason, setFaceCheckReason] = useState("");

  const handleCaptured = (captured: File) => {
    setFile(captured);
    setPreviewUrl(URL.createObjectURL(captured));
    setFaceDetected(null);
    setFaceCheckReason("");
    setVerifying(true);
    gpsDriveContractSigningService
      .verifyPhoto(token, captured)
      .then((result) => {
        setFaceDetected(result.faceDetected);
        setFaceCheckReason(result.reason);
      })
      .catch(() => setFaceDetected(null))
      .finally(() => setVerifying(false));
  };

  useEffect(() => {
    if (file) return;
    let cancelled = false;
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(true);
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
        setCameraReady(true);
      })
      .catch(() => setCameraError(true));
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setCameraReady(false);
    };
  }, [file]);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        handleCaptured(new File([blob], "assinatura.jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.9,
    );
  };

  const handlePick = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    handleCaptured(picked);
  };

  const handleRetake = () => {
    setFile(null);
    setPreviewUrl(null);
    setFaceDetected(null);
    setFaceCheckReason("");
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Tire uma foto</h1>
        <p className="mt-1 text-sm text-slate-500">Uma foto sua para confirmar a assinatura.</p>
      </div>
      <div className="flex flex-col items-center gap-3 rounded-lg bg-slate-50 px-4 py-6">
        {previewUrl ? (
          <>
            <img src={previewUrl} alt="Foto capturada" className="h-40 w-32 rounded-md object-cover shadow" />
            {verifying && <p className="text-xs text-slate-500">Verificando foto…</p>}
            {!verifying && faceDetected === false && (
              <p className="text-center text-xs text-rose-600">
                {faceCheckReason || "Não conseguimos identificar seu rosto nessa foto."} Tire outra, com boa
                iluminação e o rosto bem centralizado.
              </p>
            )}
            <Button type="button" variant="outline" size="sm" onClick={handleRetake}>
              Tirar outra foto
            </Button>
          </>
        ) : cameraError ? (
          <>
            <div className="grid h-40 w-32 place-items-center rounded-md bg-slate-200 text-slate-400">
              <CameraIcon className="h-8 w-8" />
            </div>
            <label className="cursor-pointer">
              <span className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white">
                <CameraIcon className="h-4 w-4" />
                Abrir câmera
              </span>
              <input type="file" accept="image/*" capture="user" className="hidden" onChange={handlePick} />
            </label>
          </>
        ) : (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              className="h-52 w-full max-w-xs rounded-md bg-black object-cover"
            />
            <canvas ref={canvasRef} className="hidden" />
            <Button type="button" className="w-full" disabled={!cameraReady} onClick={handleCapture}>
              <CameraIcon className="mr-2 h-4 w-4" />
              Capturar foto
            </Button>
          </>
        )}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onBack}>
          Voltar
        </Button>
        <Button
          className="flex-1"
          disabled={!file || verifying || faceDetected === false}
          onClick={() => file && onNext(file)}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}

function StepSign({
  preview,
  token,
  coords,
  photo,
  onBack,
  onSigned,
}: {
  preview: ContractSigningPreview;
  token: string;
  coords: { lat: number; lng: number } | null;
  photo: File | null;
  onBack: () => void;
  onSigned: () => void;
}) {
  const [name, setName] = useState(preview.client.name);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && agreed && !!coords && !!photo;

  const handleSubmit = async () => {
    if (!canSubmit || !coords || !photo) return;
    setSubmitting(true);
    setError(null);
    try {
      await gpsDriveContractSigningService.sign(token, {
        signedByName: name.trim(),
        latitude: coords.lat,
        longitude: coords.lng,
        photo,
      });
      onSigned();
    } catch (err) {
      setError(
        err instanceof GpsDriveApiError
          ? err.message
          : "Não foi possível concluir a assinatura. Tente novamente.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Confirme a assinatura</h1>
        <p className="mt-1 text-sm text-slate-500">Digite seu nome completo para assinar o contrato.</p>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Nome completo</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome completo" />
      </div>
      {preview.template && <ContractReaderButton template={preview.template} />}
      <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-600">
        <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
        Li e concordo com os termos do contrato apresentado.
      </label>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onBack} disabled={submitting}>
          Voltar
        </Button>
        <Button className="flex-1" disabled={!canSubmit || submitting} onClick={handleSubmit}>
          {submitting ? "Assinando…" : "Assinar contrato"}
        </Button>
      </div>
    </div>
  );
}
